import { HelpTip } from "@/components/Help";
import { InputWeightCalcModal } from "@/components/InputWeightCalcModal";
import { ProductWeightCalcModal } from "@/components/ProductWeightCalcModal";
import { Card, Field, Icon, Pill, Select, TextInput, Textarea } from "@/components/ui";
import { uploadQuoteFile } from "@/lib/api";
import {
  PART_QUOTE_TYPE_OPTIONS,
  selectionAvgInput,
  selectionAvgVolume,
  usgFactor,
  volKey,
  volumeYears,
} from "@/lib/cost/cncBreakdown";
import {
  type CncMasterData,
  applyInputWeightResult,
  generalAdminRateForCostType,
  machineRateForLabor,
  materialSelectionPatch,
  processNameOf,
  scrapRecoveryFraction,
  selectionToResult,
} from "@/lib/cost/cncForm";
import { type TreatmentUnit, lineCost } from "@/lib/cost/cncQuote";
import { NO_ALLOWANCE_PROCESSES } from "@/lib/cost/labor";
import {
  type CncPartSelection,
  type CncProcessSelection,
  type CncTreatmentLine,
  fmtDec,
  fmtKRWDec,
} from "@/lib/data";
import { useQuoteImageSrc } from "@/lib/hooks";
import { imageFileError } from "@/lib/imageFile";
import { cols, useIsMobile } from "@/lib/useViewport";
import { type CSSProperties, useMemo, useRef, useState } from "react";

// 라인 로컬 key 시퀀스 (렌더 안정용 — 계산엔 미사용).
let lineSeq = 0;
const nextLineId = (prefix: string) => {
  lineSeq += 1;
  return `${prefix}-${lineSeq}`;
};

// 신규 입력 단위는 중량당(g)/개당(EA) 두 가지. "kg"(KG당)는 시트 피드백으로 선택란에서 제거했다.
// 단, 기존 저장 견적이 kg 로 저장돼 있을 수 있어 lineCost 의 kg 경로·타입은 유지(견적 불변성).
const UNIT_OPTS: { value: TreatmentUnit; label: string }[] = [
  { value: "g", label: "중량당 (g)" },
  { value: "ea", label: "개당 (EA)" },
];

const td = (a?: "left" | "right" | "center"): CSSProperties => ({
  padding: "10px 14px",
  textAlign: a || "left",
  borderTop: "1px solid var(--bg-surface-2)",
});

const Head = ({ cols }: { cols: { t: string; a?: "left" | "right" | "center" }[] }) => (
  <thead style={{ background: "var(--bg-card-hover)" }}>
    <tr
      style={{
        color: "var(--text-dim)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {cols.map((c) => (
        <th
          key={c.t || `sp-${c.a}`}
          style={{
            textAlign: c.a || "left",
            padding: "10px 14px",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {c.t}
        </th>
      ))}
    </tr>
  </thead>
);

// 항목별 공식 도움말 (HelpTip 텍스트).
const TIP = {
  rawMaterial:
    "원재료비 = 투입중량(g) × 원소재단가(₩/g) − (투입중량 − 제품중량) × 스크랩단가 × 회수율. 투입과 제품의 중량 차이 중 회수율만큼 스크랩 매각분이 차감됩니다. 입력란의 '계산' 버튼으로 외경·길이 기반 자동 산출이 가능합니다.",
  treatment:
    "단위가 KG당(KG)이면 (제품중량 ÷ 1000) × 단가(₩/kg), 개당(EA)이면 단가가 그대로 1ea 에 부과됩니다. 중량당(g)은 기존 견적 호환용 legacy 단위로 제품중량(g) × 단가(₩/g) 입니다. 표면처리는 마스터 선택 시 단가가 자동 조회됩니다. '+ 항목 추가' 로 세부 항목을 늘릴 수 있습니다.",
  process:
    "노무비 = 인원 × C/T × 임율 ÷ 3600 × (1+여유율), 경비 = C/T × 기계경비 ÷ 3600 × (1+여유율), 가공비 = 노무비 + 경비. 여유율은 공정별로 입력할 수 있으며, 비우면 마스터 설정 기본값을 사용합니다. (예: 최종검사/포장은 0%)",
  costType:
    "내작이면 일반관리비율 17%, 외작이면 10% 가 제조원가에 적용됩니다 (엑셀 CNC 원가계산서 기준).",
  profit:
    "금액 모드는 이윤을 ₩/ea 로 직접 입력합니다. 마진율 모드는 판매가 기준 영업이익률 방식으로, 제품원가 = 내부원가(제조원가+일반관리비+재료관리비+금형비+기타비용) ÷ (1 − 마진율%), 이윤 = 제품원가 − 내부원가 입니다. (내부원가 150, 마진 10% → 150 ÷ 0.9 = 166.67)",
  mold: "양산수량 안분 체크 시 금형비(1ea) = 금형비 총액 ÷ 양산수량, 해제 시 총액이 그대로 1ea 에 부과됩니다.",
  admin:
    "일반관리비 = 제조원가 × 일반관리비율, 재료관리비 = 재료비계 × 재료관리비율. 비율은 CNC 마스터 설정 탭에서 관리합니다.",
  productCost: "제품원가(1ea) = 제조원가 + 일반관리비 + 이윤 + 재료관리비 + 금형비 + 기타비용.",
} as const;

// ── 표면처리/열처리/기타비 라인 편집 섹션 ──────────────────────────
const TreatmentSection = ({
  title,
  totalLabel,
  lines,
  onChange,
  surfaceOptions,
  productWeightG,
  total,
}: {
  title: string;
  totalLabel: string; // 우측 강조 합계 박스 라벨 (예: "표면처리비 합계")
  lines: CncTreatmentLine[];
  onChange: (next: CncTreatmentLine[]) => void;
  // 표면처리 섹션만 마스터 옵션을 받는다 (열처리/기타는 자유 입력).
  surfaceOptions?: { id: string; name: string; unitPrice: number }[];
  productWeightG: number; // 제품중량(g) — 행별 비용 표시·계산용(원가가 쓰는 값과 동일)
  total: number; // 섹션 합계 — 이미 계산된 quote.material.{surface,heat,etc}Cost 재사용
}) => {
  const setLine = (idx: number, patch: Partial<CncTreatmentLine>) =>
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const add = () =>
    onChange([
      ...lines,
      {
        id: nextLineId(surfaceOptions ? "sf" : "ln"),
        label: "",
        surfaceId: surfaceOptions ? "" : undefined,
        unit: "g",
        unitPrice: "0",
      },
    ]);
  const remove = (idx: number) => onChange(lines.filter((_, i) => i !== idx));

  const onSurfacePick = (idx: number, id: string) => {
    const s = surfaceOptions?.find((o) => o.id === id);
    setLine(idx, { surfaceId: id, label: s?.name ?? "", unitPrice: s ? String(s.unitPrice) : "0" });
  };

  return (
    <Card pad={0} style={{ overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
        <HelpTip text={TIP.treatment} />
        <div style={{ flex: 1 }} />
        <Pill variant="dark" size="sm" icon={<Icon name="plus" size={15} />} onClick={add}>
          항목 추가
        </Pill>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <Head
            cols={[
              { t: surfaceOptions ? "표면처리" : "항목명" },
              { t: "단위" },
              { t: "제품중량 (g)", a: "right" },
              { t: "단가 (G/EA 당 단가)", a: "right" },
              { t: title, a: "right" }, // 행별 비용 = 제품중량 × 단가 (lineCost)
              { t: "", a: "center" },
            ]}
          />
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{ ...td("center"), color: "var(--text-faint)", padding: "20px 0" }}
                >
                  '항목 추가' 로 항목을 등록하세요
                </td>
              </tr>
            )}
            {lines.map((l, idx) => (
              <tr key={l.id}>
                <td style={{ ...td(), minWidth: 200 }}>
                  {surfaceOptions ? (
                    <Select
                      value={l.surfaceId ?? ""}
                      onChange={(v) => onSurfacePick(idx, v)}
                      options={surfaceOptions.map((o) => ({ value: o.id, label: o.name }))}
                      placeholder="선택"
                      style={{ height: 36 }}
                    />
                  ) : (
                    <TextInput
                      value={l.label}
                      onChange={(v) => setLine(idx, { label: v })}
                      placeholder="항목명"
                      style={{ height: 36 }}
                    />
                  )}
                </td>
                {/* 단위 컬럼 — 라벨이 드롭다운 화살표와 겹치지 않도록 폭 확보(시트 글자겹침 피드백) */}
                <td style={{ ...td(), minWidth: 132, width: 132 }}>
                  <Select
                    value={l.unit}
                    onChange={(v) =>
                      // 단위 변경 시 단가 초기화 — ₩/kg·₩/g·₩/ea 는 크기 스케일이 달라
                      // 이전 값을 그대로 두면 오계산되므로 재입력을 강제한다.
                      setLine(idx, v === l.unit ? {} : { unit: v as TreatmentUnit, unitPrice: "0" })
                    }
                    // 신규 단위는 g/EA(UNIT_OPTS). 단, 이미 kg 로 저장된 기존 행은 legacy 옵션을
                    // 노출해야 화면 표시와 계산(lineCost 의 kg 경로)이 일치한다(견적 불변성).
                    options={
                      l.unit === "kg"
                        ? [
                            ...UNIT_OPTS,
                            { value: "kg" as TreatmentUnit, label: "KG당 (KG) — 기존" },
                          ]
                        : UNIT_OPTS
                    }
                    style={{ height: 36 }}
                  />
                </td>
                {/* 제품중량(g) — 읽기전용, 원가가 쓰는 값과 동일 */}
                <td style={{ ...td("right"), fontFamily: "monospace", color: "var(--text-near)" }}>
                  {fmtDec(productWeightG)}
                </td>
                <td style={{ ...td("right"), minWidth: 140 }}>
                  <TextInput
                    value={l.unitPrice}
                    onChange={(v) => setLine(idx, { unitPrice: v })}
                    type="number"
                    mono
                    align="right"
                    style={{ height: 36 }}
                  />
                </td>
                {/* 행별 비용 — 읽기전용. lib lineCost 재사용(kg→/1000), JSX 인라인 공식 금지 */}
                <td style={{ ...td("right"), fontFamily: "monospace", fontWeight: 700 }}>
                  {fmtDec(
                    lineCost(
                      { unit: l.unit, unitPrice: Number.parseFloat(l.unitPrice) || 0 },
                      productWeightG
                    )
                  )}
                </td>
                <td style={td("center")}>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    title="삭제"
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "var(--text-dim)",
                      cursor: "pointer",
                      display: "inline-flex",
                    }}
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* 섹션 합계 — 우측 강조. 이미 계산된 quote.material.{surface,heat,etc}Cost 재사용. */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
          padding: "12px 20px",
          borderTop: "1px solid var(--bg-surface-2)",
          background: "var(--bg-card-hover)",
        }}
      >
        <span style={{ fontSize: 12.5, color: "var(--text-muted)", fontWeight: 700 }}>
          {totalLabel}
        </span>
        <span
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 900,
            fontSize: 18,
            color: "var(--brand)",
          }}
        >
          {fmtKRWDec(total)}
        </span>
      </div>
    </Card>
  );
};

// 중량 입력 + 계산 팝업 트리거.
const WeightField = ({
  label,
  value,
  onChange,
  onCalc,
  tip,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onCalc: () => void;
  tip?: string;
}) => (
  <Field label={label} en="g">
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <TextInput value={value} onChange={onChange} type="number" mono style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onCalc}
        title="중량 계산"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "0 12px",
          height: 44,
          borderRadius: 12,
          border: 0,
          background: "var(--bg-inverse)",
          color: "var(--text-inverse)",
          fontWeight: 700,
          fontSize: 12.5,
          cursor: "pointer",
          fontFamily: "inherit",
          whiteSpace: "nowrap",
        }}
      >
        <Icon name="grid" size={14} /> 계산
      </button>
      {tip && <HelpTip text={tip} />}
    </div>
  </Field>
);

// 입력 옆에 붙는 읽기전용 계산값 표시(₩, 소수점 2자리). 값은 항상 이미 계산된
// quote.material.* 를 받아 렌더만 한다 — 컴포넌트에서 재계산하지 않는다.
const ReadField = ({
  label,
  en,
  value,
  hint,
}: {
  label: string;
  en?: string;
  value: number;
  hint?: string;
}) => (
  <Field label={label} en={en} hint={hint}>
    <div
      style={{
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 14px",
        borderRadius: 12,
        background: "var(--bg-surface-2)",
        fontFamily: "monospace",
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {fmtKRWDec(value)}
    </div>
  </Field>
);

export const CncQuoteForm = ({
  value,
  onChange,
  master,
}: {
  value: CncPartSelection;
  onChange: (next: CncPartSelection) => void;
  master: CncMasterData;
}) => {
  const mobile = useIsMobile();
  const [modal, setModal] = useState<null | "input" | "product">(null);
  const imgInputRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const partImgUrl = useQuoteImageSrc(value.partImage ?? "");

  // 파트 이미지 업로드 — 검증 후 오브젝트 스토리지(quotes/files)에 올리고 selection.partImage
  // 에는 스토리지 키만 저장(견적과 함께 영속). 표시는 useQuoteImageSrc 가 blob URL 로 변환.
  const onPartImage = async (file: File | undefined) => {
    if (!file) return;
    const err = imageFileError(file, "파트 이미지");
    setImgError(err);
    if (err) return;
    setImgBusy(true);
    try {
      const att = await uploadQuoteFile(file);
      onChange({ ...value, partImage: att.key });
    } catch (e) {
      setImgError(e instanceof Error ? e.message : "이미지 업로드에 실패했습니다");
    } finally {
      setImgBusy(false);
    }
  };

  const set = <K extends keyof CncPartSelection>(key: K, v: CncPartSelection[K]) =>
    onChange({ ...value, [key]: v });

  // 재질 선택 → 원소재/스크랩 단가 + 재질 회수율 자동(기존 견적은 회수율 미적용 유지).
  const onMaterialChange = (id: string) =>
    onChange({ ...value, ...materialSelectionPatch(value, id, master) });

  const onRowProcessChange = (idx: number, laborRateId: string) => {
    const lr = master.labor.find((r) => r.id === laborRateId);
    const rate = machineRateForLabor(laborRateId, master);
    set(
      "rows",
      value.rows.map((row, i) =>
        i === idx
          ? {
              ...row,
              laborRateId,
              machineRate: String(rate),
              headcount: lr?.headcount ?? 0,
              hourlyLaborRate: lr?.hourlyRate ?? 0,
            }
          : row
      )
    );
  };

  const setRowField = (idx: number, key: "ctSec" | "machineRate", v: string) =>
    set(
      "rows",
      value.rows.map((row, i) => (i === idx ? { ...row, [key]: v } : row))
    );

  // 공정별 여유율(%) 편집 — 빈 값이면 part 기본(value.allowance)으로 되돌린다(undefined).
  const setRowAllowance = (idx: number, pct: string) => {
    const trimmed = pct.trim();
    const allowance = trimmed === "" ? undefined : (Number.parseFloat(trimmed) || 0) / 100;
    set(
      "rows",
      value.rows.map((row, i) => (i === idx ? { ...row, allowance } : row))
    );
  };

  // 내작/외작 전환 — 일반관리비율(17%/10%)을 동시에 resolve 한다.
  const onCostTypeChange = (costType: CncPartSelection["costType"]) =>
    onChange({ ...value, costType, generalAdminRate: generalAdminRateForCostType(costType) });

  const addRow = () =>
    set("rows", [
      ...value.rows,
      {
        laborRateId: "",
        ctSec: "",
        machineRate: "0",
        headcount: 0,
        hourlyLaborRate: 0,
      } as CncProcessSelection,
    ]);

  const removeRow = (idx: number) =>
    set(
      "rows",
      value.rows.filter((_, i) => i !== idx)
    );

  const quote = useMemo(() => selectionToResult(value, master), [value, master]);

  // 제품중량(g) — 표면/열/기타 라인 비용이 쓰는 값과 동일. TreatmentSection 행별 비용·표시용.
  const productWeightG = Number.parseFloat(value.productWeight) || 0;

  // 스크랩중량(g) — 입력값이 있으면 그대로, 미입력(빈 값)이면 투입−제품 자동. 스크랩비 보조 라벨/
  // 플레이스홀더용 유효값(원가 계산은 selectionToInput 이 동일 규칙으로 처리 — 여기선 표시 전용).
  const derivedScrapWeightG = (Number.parseFloat(value.inputWeight) || 0) - productWeightG;
  const scrapWeightG =
    value.scrapWeight.trim() !== ""
      ? Number.parseFloat(value.scrapWeight) || 0
      : derivedScrapWeightG;
  // 스크랩 회수율(%) — 필드 없는 기존 견적은 100%(내용추가18: 새 견적부터만 적용). 표시 전용.
  const legacyRecovery = value.scrapRecoveryRate === undefined;
  const recoveryPct = scrapRecoveryFraction(value.scrapRecoveryRate) * 100;

  // 산출 내역 행(원소재 → 차감 → 가공별 부가 → 소계). '재료비계'(원재료비+표면+열+기타 = materialCost)는
  // 가공비계처럼 소계로 굵게 표기한다(시트 산출내역 피드백). 맨 아래 강조행(내부원가 합계 →
  // 이윤 → 제품단가 최종 합계)은 별도 분리(아래 JSX) — 고객 목업 순서(내용추가 피드백).
  const breakdown: [string, number][] = [
    ["원소재비", quote.material.rawMaterialCost],
    ["스크랩비 (차감)", quote.material.scrapCredit],
    ["원재료비", quote.material.netMaterialCost],
    ["표면처리비", quote.material.surfaceCost],
    ["열처리비", quote.material.heatCost],
    ["기타비", quote.material.etcCost],
    // 재료비계 = 원재료비 + 표면처리비 + 열처리비 + 기타비(= materialCost). 가공비계처럼 소계 굵게
    // 표기(라벨이 "계"로 끝나 아래 렌더 로직에서 자동 bold). (시트 산출내역 피드백)
    ["재료비계", quote.material.materialCost],
    ["노무비계", quote.processing.laborTotal],
    ["경비계", quote.processing.expenseTotal],
    ["가공비계", quote.processing.processingTotal],
    ["제조원가", quote.manufacturingCost],
    ["일반관리비", quote.generalAdmin],
    ["재료관리비", quote.materialAdmin],
    ["금형비", quote.moldCost],
    ["기타비용", quote.miscCost],
  ];

  // 내부원가 합계 = 제조원가+일반관리비+재료관리비+금형비+기타비용 — 엔진(cncQuote)의
  // internalBeforeProfit 과 동일한 합(표시 전용 합산, 원가 재계산 아님).
  // 내부원가 합계 + 이윤 = 제품단가 최종 합계(quote.productCost).
  const internalTotal =
    quote.manufacturingCost +
    quote.generalAdmin +
    quote.materialAdmin +
    quote.moldCost +
    quote.miscCost;

  const addends: [string, number][] = [
    ["제조원가", quote.manufacturingCost],
    ["일반관리비", quote.generalAdmin],
    ["이윤", quote.profit],
    ["재료관리비", quote.materialAdmin],
    ["금형비", quote.moldCost],
    ["기타비용", quote.miscCost],
  ];

  const setVolume = (key: string, v: string) =>
    onChange({ ...value, volumes: { ...value.volumes, [key]: v } });

  // 동적 연도(기본 2027~2031, +연도 추가). 연평균은 입력값 평균(USG 미곱), 셀은 입력×USG 병기.
  const years = volumeYears(value.volumes);
  const usg = usgFactor(value);
  const avgInput = selectionAvgInput(value);
  const avgVolume = selectionAvgVolume(value); // 연평균 × USG — 빨간 보조 라벨용(고객 요청)
  const hasVolumes = years.some((y) => (value.volumes[volKey(y)] ?? "").trim() !== "");
  const avgLabel = years.length ? `연평균 (${years[0]}~${years[years.length - 1]})` : "연평균";
  const addYear = () => {
    const next = (years.length ? years[years.length - 1] : 2026) + 1;
    onChange({ ...value, volumes: { ...value.volumes, [volKey(next)]: "" } });
  };
  // 연도는 칸 단위로만 삭제(라벨은 고정 표시). 원치 않는 연도를 지우고 "연도 추가"로 뒤에 붙인다.
  const removeYearAt = (y: number) => {
    if (years.length <= 1) return;
    const rest = { ...value.volumes };
    delete rest[volKey(y)];
    onChange({ ...value, volumes: rest });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* 0. 파트 헤더 — 품목명, 견적유형, 연도별 물량, USG 수량 */}
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 18px" }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16 }}>파트 정보</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 14,
            }}
          >
            {/* 품목명·품번은 개행 허용 — 긴 값(예: "M5 INSERT NUT(단조 원가절감 제안)")을 줄바꿈해서
                제출 견적서 PDF 까지 그대로 출력한다. */}
            <Field label="품목명" en="Item Name">
              <Textarea
                value={value.itemName}
                onChange={(v) => set("itemName", v)}
                placeholder="예: 로봇 관절 브래킷"
                rows={2}
              />
            </Field>
            <Field label="품번" en="Part No">
              <Textarea
                value={value.partNo ?? ""}
                onChange={(v) => set("partNo", v)}
                placeholder="예: JW-10234"
                rows={2}
              />
            </Field>
            <Field label="견적유형" en="Quote Type">
              <Select
                value={value.partQuoteType}
                onChange={(v) => set("partQuoteType", v)}
                placeholder="견적 유형 선택"
                options={PART_QUOTE_TYPE_OPTIONS}
              />
            </Field>
            {/* 파트 이미지 — 견적상세 도면/사진·제출견적서 이미지 컬럼에 자동 연동 출력. */}
            <Field label="파트 이미지" en="Part Image">
              <div style={{ display: "flex", alignItems: "center", gap: 10, height: 44 }}>
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    void onPartImage(e.target.files?.[0]);
                    e.target.value = ""; // 같은 파일 재선택도 change 발생하도록 초기화
                  }}
                />
                {partImgUrl && (
                  <img
                    src={partImgUrl}
                    alt="파트 이미지"
                    style={{
                      height: 44,
                      maxWidth: 78,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid var(--bg-surface-3)",
                    }}
                  />
                )}
                <Pill
                  variant="ghost"
                  size="sm"
                  disabled={imgBusy}
                  onClick={() => imgInputRef.current?.click()}
                >
                  {imgBusy ? "업로드 중…" : value.partImage ? "이미지 변경" : "이미지 업로드"}
                </Pill>
                {value.partImage && (
                  <Pill
                    variant="ghost"
                    size="sm"
                    icon={<Icon name="trash" size={14} />}
                    onClick={() => set("partImage", "")}
                  >
                    삭제
                  </Pill>
                )}
              </div>
              {imgError && (
                <div style={{ fontSize: 11.5, color: "var(--text-negative)" }}>{imgError}</div>
              )}
            </Field>
          </div>
          {/* 연도별 물량 정보 — 동적 연도(기본 2027~2031, +연도 추가). 셀은 입력값 + (입력×USG) 병기. */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                물량 정보 (EA/년)
              </div>
              <div style={{ flex: 1 }} />
              <Pill
                variant="dark"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={addYear}
              >
                연도 추가
              </Pill>
            </div>
            <div style={{ overflowX: "auto", paddingBottom: 4 }}>
              <div style={{ display: "flex", gap: 8, minWidth: "min-content" }}>
                {years.map((y) => {
                  const raw = value.volumes[volKey(y)] ?? "";
                  const eff = (Number.parseFloat(raw) || 0) * usg;
                  return (
                    <div key={y} style={{ minWidth: 116, flex: "1 0 116px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {/* 연도 라벨 = 고정 표시. 편집 대신 칸 단위 삭제(×) — 원치 않는 연도를
                            지우고 "연도 추가"로 뒤에 붙인다. */}
                        <div
                          style={{
                            fontSize: 12.5,
                            color: "var(--text-muted)",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{y}년</span>
                          {years.length > 1 && (
                            <button
                              type="button"
                              aria-label={`${y}년 삭제`}
                              onClick={() => removeYearAt(y)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                background: "transparent",
                                border: 0,
                                padding: 2,
                                cursor: "pointer",
                                color: "var(--text-subtle)",
                              }}
                            >
                              <Icon name="x" size={13} />
                            </button>
                          )}
                        </div>
                        <TextInput
                          value={raw}
                          onChange={(v) => setVolume(volKey(y), v)}
                          type="number"
                          mono
                        />
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontFamily: "ui-monospace, monospace",
                          color: "var(--text-negative)",
                          textAlign: "right",
                        }}
                      >
                        {raw.trim() ? `${eff.toLocaleString("ko-KR")}개` : " "}
                      </div>
                    </div>
                  );
                })}
                <div style={{ minWidth: 132, flex: "1 0 132px" }}>
                  <Field label={avgLabel}>
                    <div
                      aria-label="연평균 (자동계산 · USG 미곱)"
                      style={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        borderRadius: 8,
                        background: "var(--bg-card)",
                        color: "var(--text-muted)",
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 13.5,
                      }}
                    >
                      {avgInput ? `${Math.round(avgInput).toLocaleString("ko-KR")}개` : "-"}
                    </div>
                  </Field>
                  {/* 연평균 × USG — 연도 셀의 빨간 보조 라벨과 동일 스타일(고객 요청: USG 곱한
                      물량의 연평균도 빨간색으로). 예: 3만·3만·1만·1만 × USG2 → 연평균 4만개. */}
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      fontFamily: "ui-monospace, monospace",
                      color: "var(--text-negative)",
                      textAlign: "right",
                    }}
                  >
                    {avgInput ? `${Math.round(avgVolume).toLocaleString("ko-KR")}개` : " "}
                  </div>
                </div>
                {/* 라벨("USG 수량 USG Qty")이 개행되지 않도록 nowrap + 라벨 폭 확보(고객 정렬 요청). */}
                <div style={{ minWidth: 128, flex: "0 0 128px" }}>
                  <Field label="USG 수량" en="USG Qty" style={{ whiteSpace: "nowrap" }}>
                    <TextInput
                      value={value.usgQty}
                      onChange={(v) => set("usgQty", v)}
                      type="number"
                      mono
                    />
                  </Field>
                </div>
              </div>
            </div>
            {!hasVolumes && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-faint)" }}>
                연도별 물량 정보가 없습니다.
              </div>
            )}
          </div>
        </div>
      </Card>
      {/* 1. 재료비 (재질 · 중량 · 단가) — 외경은 투입중량 계산 모달에서만 입력.
          입력 옆에 계산값(원소재비·스크랩비)과 우측 강조 「재료비 합계」를 함께 표기(시트 요청). */}
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 18px" }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16 }}>재료비</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 14,
            }}
          >
            <Field label="재질" en="Material">
              <Select
                value={value.materialId}
                onChange={onMaterialChange}
                options={master.raw.map((m) => ({ value: m.id, label: m.name }))}
                placeholder="선택"
              />
            </Field>
            <WeightField
              label="투입중량"
              value={value.inputWeight}
              onChange={(v) => set("inputWeight", v)}
              onCalc={() => setModal("input")}
              tip={TIP.rawMaterial}
            />
            <WeightField
              label="제품중량"
              value={value.productWeight}
              onChange={(v) => set("productWeight", v)}
              onCalc={() => setModal("product")}
            />
            <Field label="원소재 단가" en="₩/g" hint="재질 선택 시 자동 — 수정 가능">
              <TextInput
                value={value.rawPrice}
                onChange={(v) => set("rawPrice", v)}
                type="number"
                mono
              />
            </Field>
            {/* 원소재비 = 투입중량 × 원소재단가 — quote.material.rawMaterialCost 재사용(재계산 X) */}
            <ReadField
              label="원소재비"
              en="투입중량 × 원소재단가"
              value={quote.material.rawMaterialCost}
            />
            {/* 스크랩중량 — 독립 입력. 미입력 시 투입−제품 자동(placeholder 로 표시, state 엔 쓰지 않음). */}
            <Field label="스크랩중량" en="g" hint="미입력 시 투입−제품 자동">
              <TextInput
                value={value.scrapWeight}
                onChange={(v) => set("scrapWeight", v)}
                type="number"
                mono
                placeholder={fmtDec(derivedScrapWeightG)}
              />
            </Field>
            <Field label="스크랩 단가" en="₩/g" hint="재질 선택 시 자동 — 수정 가능">
              <TextInput
                value={value.scrapPrice}
                onChange={(v) => set("scrapPrice", v)}
                type="number"
                mono
              />
            </Field>
            {/* 회수율(%) — 재질별 기본값(90) 자동 + 수정 가능. 기존 견적(필드 없음)은 100% 유지. */}
            <Field
              label="회수율"
              en="%"
              hint={
                legacyRecovery
                  ? "기존 견적 — 미적용(100%) · 입력 시 적용"
                  : "재질 선택 시 자동(기본 90%) — 수정 가능"
              }
            >
              <TextInput
                value={value.scrapRecoveryRate ?? ""}
                onChange={(v) => set("scrapRecoveryRate", v)}
                type="number"
                mono
                placeholder="100"
                suffix="%"
              />
            </Field>
            {/* 스크랩비 = 스크랩중량 × 스크랩단가 × 회수율 — quote.material.scrapCredit 재사용(재계산 X) */}
            <ReadField
              label="스크랩비"
              en="스크랩중량 × 스크랩단가 × 회수율"
              value={quote.material.scrapCredit}
              hint={`${fmtDec(scrapWeightG)} g × ${value.scrapPrice || 0} ₩/g × ${recoveryPct.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}%`}
            />
          </div>
        </div>
        {/* 재료비 합계 = 원소재비 − 스크랩비 (= quote.material.netMaterialCost). 표면/열처리/기타
            섹션 하단 합계 줄과 동일한 포맷으로 통일. 값은 이미 계산된 결과를 재사용(재계산 X). */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderTop: "1px solid var(--bg-surface-2)",
            background: "var(--bg-card-hover)",
          }}
        >
          <span style={{ fontSize: 12.5, color: "var(--text-muted)", fontWeight: 700 }}>
            재료비 합계
          </span>
          <span
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 900,
              fontSize: 18,
              color: "var(--brand)",
            }}
          >
            {fmtKRWDec(quote.material.netMaterialCost)}
          </span>
        </div>
      </Card>

      {/* 2~4. 표면처리비 / 열처리비 / 기타비 + 전체 합계 — 세로로 길어 한눈에 안 들어온다는 시트
          피드백에 따라 2열 그리드로 묶어 컴팩트하게 배치(좁은 화면에선 1열로 자동 폴백). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols(mobile, "repeat(2, minmax(0, 1fr))"),
          gap: 18,
          alignItems: "start",
        }}
      >
        <TreatmentSection
          title="표면처리비"
          totalLabel="표면처리비 합계"
          lines={value.surfaceLines}
          onChange={(next) => set("surfaceLines", next)}
          surfaceOptions={master.surface.map((s) => ({
            id: s.id,
            name: s.name,
            unitPrice: s.unitPrice,
          }))}
          productWeightG={productWeightG}
          total={quote.material.surfaceCost}
        />
        <TreatmentSection
          title="열처리비"
          totalLabel="열처리비 합계"
          lines={value.heatLines}
          onChange={(next) => set("heatLines", next)}
          productWeightG={productWeightG}
          total={quote.material.heatCost}
        />
        <TreatmentSection
          title="기타비"
          totalLabel="기타비 합계"
          lines={value.etcLines}
          onChange={(next) => set("etcLines", next)}
          productWeightG={productWeightG}
          total={quote.material.etcCost}
        />

        {/* 전체 합계 — 재료비합계 + 표면 + 열 + 기타 = 재료비 최종 합계(materialCost). 값 재계산 X. */}
        <Card pad={0} style={{ overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <Head cols={[{ t: "전체 합계" }, { t: "금액 (₩/ea)", a: "right" }]} />
            <tbody>
              {(
                [
                  ["재료비 합계", quote.material.netMaterialCost],
                  ["표면처리비 합계", quote.material.surfaceCost],
                  ["열처리비 합계", quote.material.heatCost],
                  ["기타비 합계", quote.material.etcCost],
                ] as [string, number][]
              ).map(([label, val]) => (
                <tr key={label}>
                  <td style={td()}>{label}</td>
                  <td style={{ ...td("right"), fontFamily: "monospace" }}>{fmtDec(val)}</td>
                </tr>
              ))}
              <tr style={{ background: "var(--bg-card-hover)" }}>
                <td style={{ ...td(), fontWeight: 800 }}>재료비 최종 합계</td>
                <td style={{ ...td("right"), fontFamily: "monospace", fontWeight: 800 }}>
                  {fmtDec(quote.material.materialCost)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      {/* 5. 공정 */}
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>공정</div>
          <HelpTip text={TIP.process} />
          <div style={{ flex: 1 }} />
          <Pill variant="dark" size="sm" icon={<Icon name="plus" size={15} />} onClick={addRow}>
            공정 추가
          </Pill>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <Head
              cols={[
                { t: "공정" },
                { t: "인원", a: "right" },
                { t: "임율 (₩/hr)", a: "right" },
                { t: "C/T (초)", a: "right" },
                { t: "기계경비 (₩/hr)", a: "right" },
                { t: "여유율 (%)", a: "right" },
                { t: "노무비", a: "right" },
                { t: "경비", a: "right" },
                { t: "가공비", a: "right" },
                { t: "", a: "center" },
              ]}
            />
            <tbody>
              {value.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{ ...td("center"), color: "var(--text-faint)", padding: "22px 0" }}
                  >
                    '공정 추가' 로 공정을 등록하세요
                  </td>
                </tr>
              )}
              {value.rows.map((row, idx) => {
                const per = quote.processing.perProcess[idx];
                // 여유율 미적용 공정(최종검사/포장)은 입력을 0 으로 고정·비활성화한다. (#79)
                const noAllowance = NO_ALLOWANCE_PROCESSES.has(
                  processNameOf(row.laborRateId, master)
                );
                return (
                  <tr key={idx}>
                    <td style={{ ...td(), minWidth: 180 }}>
                      <Select
                        value={row.laborRateId}
                        onChange={(v) => onRowProcessChange(idx, v)}
                        options={master.labor.map((r) => ({ value: r.id, label: r.process }))}
                        placeholder="선택"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td
                      style={{ ...td("right"), fontFamily: "monospace", color: "var(--text-near)" }}
                    >
                      {row.laborRateId ? row.headcount : "—"}
                    </td>
                    <td
                      style={{ ...td("right"), fontFamily: "monospace", color: "var(--text-near)" }}
                    >
                      {row.laborRateId ? fmtDec(row.hourlyLaborRate) : "—"}
                    </td>
                    <td style={{ ...td("right"), minWidth: 110 }}>
                      <TextInput
                        value={row.ctSec}
                        onChange={(v) => setRowField(idx, "ctSec", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...td("right"), minWidth: 130 }}>
                      <TextInput
                        value={row.machineRate}
                        onChange={(v) => setRowField(idx, "machineRate", v)}
                        type="number"
                        mono
                        align="right"
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...td("right"), minWidth: 90 }}>
                      <TextInput
                        value={
                          noAllowance
                            ? "0"
                            : row.allowance !== undefined
                              ? String(row.allowance * 100)
                              : ""
                        }
                        onChange={(v) => setRowAllowance(idx, v)}
                        type="number"
                        mono
                        align="right"
                        disabled={noAllowance}
                        placeholder={noAllowance ? "0" : String(value.allowance * 100)}
                        style={{ height: 36 }}
                      />
                    </td>
                    <td style={{ ...td("right"), fontFamily: "monospace" }}>
                      {per ? fmtDec(per.laborCost) : "—"}
                    </td>
                    <td style={{ ...td("right"), fontFamily: "monospace" }}>
                      {per ? fmtDec(per.expenseCost) : "—"}
                    </td>
                    <td style={{ ...td("right"), fontFamily: "monospace", fontWeight: 700 }}>
                      {per ? fmtDec(per.processingCost) : "—"}
                    </td>
                    <td style={td("center")}>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        title="삭제"
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "var(--text-dim)",
                          cursor: "pointer",
                          display: "inline-flex",
                        }}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 6~7. 기타 원가(좌) + 산출 내역(우) — 표 옆 배치 (#76) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
        {/* 6. 기타 원가 (이윤 · 금형비 · 기타비용) */}
        <Card style={{ flex: "2 1 420px", minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16 }}>기타 원가</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
              gap: 14,
            }}
          >
            <Field label="구분" en="내작/외작" hint="일반관리비율: 내작 17% / 외작 10%">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Select
                  value={value.costType}
                  onChange={(v) => onCostTypeChange(v as CncPartSelection["costType"])}
                  options={[
                    { value: "inhouse", label: "내작 (17%)" },
                    { value: "outsourced", label: "외작 (10%)" },
                  ]}
                  style={{ flex: 1 }}
                />
                <HelpTip text={TIP.costType} />
              </div>
            </Field>
            <Field label="이윤" en={value.profitMode === "rate" ? "% (판매가 기준)" : "₩/ea"}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Select
                  value={value.profitMode}
                  onChange={(v) => set("profitMode", v as CncPartSelection["profitMode"])}
                  options={[
                    { value: "amount", label: "금액" },
                    { value: "rate", label: "마진율" },
                  ]}
                  style={{ width: 96 }}
                />
                {value.profitMode === "rate" ? (
                  <TextInput
                    value={value.profitRate}
                    onChange={(v) => set("profitRate", v)}
                    type="number"
                    mono
                    align="right"
                    style={{ flex: 1 }}
                  />
                ) : (
                  <TextInput
                    value={value.profit}
                    onChange={(v) => set("profit", v)}
                    type="number"
                    mono
                    align="right"
                    style={{ flex: 1 }}
                  />
                )}
                <HelpTip text={TIP.profit} />
              </div>
            </Field>
            <Field label="금형비 (총액)" en="₩">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <TextInput
                  value={value.moldTotal}
                  onChange={(v) => set("moldTotal", v)}
                  type="number"
                  mono
                  style={{ flex: 1 }}
                />
                <HelpTip text={TIP.mold} />
              </div>
            </Field>
            <Field label="양산수량 안분" en="Amortize">
              <div style={{ display: "flex", alignItems: "center", gap: 10, height: 44 }}>
                <input
                  type="checkbox"
                  checked={value.amortize}
                  onChange={(e) => set("amortize", e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: "var(--brand)" }}
                />
                <TextInput
                  value={value.productionQty}
                  onChange={(v) => set("productionQty", v)}
                  type="number"
                  mono
                  placeholder="양산수량"
                  style={{ flex: 1, opacity: value.amortize ? 1 : 0.45 }}
                />
              </div>
            </Field>
            <Field label="기타비용" en="₩/ea">
              <TextInput
                value={value.miscCost}
                onChange={(v) => set("miscCost", v)}
                type="number"
                mono
              />
            </Field>
          </div>
        </Card>

        {/* 7. 산출 내역 */}
        <Card pad={0} style={{ flex: "1 1 300px", minWidth: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            산출 내역
            <HelpTip text={TIP.admin} />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <Head cols={[{ t: "항목" }, { t: "금액 (₩/ea)", a: "right" }]} />
            <tbody>
              {breakdown.map(([label, val]) => (
                <tr key={label}>
                  <td
                    style={{
                      ...td(),
                      fontWeight: label.endsWith("계") || label === "제조원가" ? 700 : 400,
                    }}
                  >
                    {label}
                  </td>
                  <td style={{ ...td("right"), fontFamily: "monospace" }}>{fmtDec(val)}</td>
                </tr>
              ))}
              {/* 내부원가 합계(강조) → 이윤 → 제품단가 최종 합계(강조) — 고객 목업 순서.
                  내부원가 합계 + 이윤 = 제품단가 최종 합계(= quote.productCost, 엔진 값 그대로). */}
              <tr style={{ background: "var(--brand-soft)" }}>
                <td style={{ ...td(), fontWeight: 800 }}>내부원가 합계</td>
                <td style={{ ...td("right"), fontFamily: "monospace", fontWeight: 800 }}>
                  {fmtDec(internalTotal)}
                </td>
              </tr>
              <tr>
                <td style={td()}>이윤</td>
                <td style={{ ...td("right"), fontFamily: "monospace" }}>{fmtDec(quote.profit)}</td>
              </tr>
              {/* 제품단가 최종 합계 — 산출 내역 전 항목의 끝값. 라벨 의미에 맞춰 제품원가(1ea,
                  = 내부원가 합계 + 이윤 = quote.productCost)를 표기한다.
                  (시트 산출내역 피드백: '재료비 최종 합계' → '제품단가 최종 합계') */}
              <tr style={{ background: "var(--brand-soft)" }}>
                <td style={{ ...td(), fontWeight: 800 }}>제품단가 최종 합계</td>
                <td style={{ ...td("right"), fontFamily: "monospace", fontWeight: 800 }}>
                  {fmtDec(quote.productCost)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      {/* 8. 결과 */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            color: "var(--text-muted)",
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          제품원가 (1ea)
          <HelpTip text={TIP.productCost} />
        </div>
        <div
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 900,
            fontSize: 34,
            color: "var(--brand)",
            marginBottom: 16,
          }}
        >
          {fmtKRWDec(quote.productCost)}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
          }}
        >
          {addends.map(([label, val]) => (
            <div
              key={label}
              style={{ background: "var(--bg-surface-2)", borderRadius: 12, padding: "12px 14px" }}
            >
              <div style={{ fontSize: 11.5, color: "var(--text-dim)", marginBottom: 4 }}>
                {label}
              </div>
              <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14.5 }}>
                {fmtDec(val)} 원
              </div>
            </div>
          ))}
        </div>
      </Card>

      {modal === "input" && (
        <InputWeightCalcModal
          master={master}
          initialMaterialId={value.materialId}
          initialOuterD={value.outerDiameter}
          initialProductLength={value.productLength}
          initialProductWeight={value.productWeight}
          initialRawPrice={value.rawPrice}
          initialStockLength={value.stockLength}
          initialRemnantLength={value.remnantLength}
          onClose={() => setModal(null)}
          // 재질 변경 시에만 단가/회수율 자동, 팝업 원자재 원가 수정 시 원소재 단가 연동 — 순수 병합.
          onApply={(r) => onChange(applyInputWeightResult(value, r, master))}
        />
      )}
      {modal === "product" && (
        <ProductWeightCalcModal
          master={master}
          materialId={value.materialId}
          initialRows={value.productWeightRows}
          initialOuterD={value.outerDiameter}
          initialLength={value.productLength}
          onClose={() => setModal(null)}
          onApply={({ productWeight, rows }) =>
            onChange({ ...value, productWeight, productWeightRows: rows })
          }
        />
      )}
    </div>
  );
};
